<template>
    <flex-container
        :gap="Size.sm"
        justify-content="center"
    >
        <flex-container
            v-for="rule in rules.data"
            :key="rule.id"
            no-wrap
            class="rule"
        >
            <flex-container :size="9">
                <p>
                    <span>
                        Stuur
                        <text-decorator bold>
                            {{ rule.frequencies.map((ruleFrequency) => ruleFrequency.label).join(', ') }}
                        </text-decorator>
                        een email
                    </span><br>
                    <span>
                        Analyseer
                        <text-decorator bold>
                            {{ rule.teams.slice(0, 3).map((team) => team.name).join(', ') }}
                            <template v-if="rule.teams.length > 3">
                                (+ nog {{ rule.teams.slice(3, rule.teams.length).length }})
                            </template>
                        </text-decorator>
                    </span><br>
                    <span>
                        In
                        <text-decorator bold>
                            <template v-if="rule.pipelines.length == 0">
                                alle pijplijnen
                            </template>
                            <template v-else>
                                {{ rule.pipelines.slice(0, 3).map((pipeline) => pipeline.name).join(', ') }}
                                <template v-if="rule.pipelines.length > 3">
                                    (+ nog {{ rule.pipelines.slice(3, rule.pipelines.length).length }})
                                </template>
                            </template>
                        </text-decorator>
                    </span><br>
                    <span>
                        Vanuit de rol van
                        <text-decorator bold>
                            {{ rule.role.name }}
                        </text-decorator>
                    </span><br>
                    <span>
                        Aan
                        <text-decorator bold>
                            {{ rule.recipients.slice(0, 3).map((user) => user.name.fullName).join(', ') }}
                            <template v-if="rule.recipients.length > 3">
                                (+ nog {{ rule.recipients.slice(3, rule.recipients.length).length }})
                            </template>
                        </text-decorator>
                    </span>
                </p>
            </flex-container>
            <flex-container
                :size="1"
                direction="column"
                :gap="Size.sm"
                align-content="flex-end"
                justify-content="center"
            >
                <button-plain-icon
                    v-tooltip="'Verwijderen'"
                    :icon="RemoveIcon"
                    :color="Color.red500"
                    @click="confirmDeleteRule(rule as Rule)"
                >
                    Verwijderen
                </button-plain-icon>
                <button-plain-icon
                    v-tooltip="'Aanpassen'"
                    :icon="EditIcon"
                    @click="router.push({ name: 'settings-virtual-colleague-ai-rules-edit', params: { ruleId: rule.id.value } })"
                >
                    Aanpassen
                </button-plain-icon>
            </flex-container>
        </flex-container>
    </flex-container>
    <confirmation-dialog
        ref="deleteRuleConfirmDialog"
        label="Verwijder een regel"
        description="Bevestig hier of je daadwerkelijk de regel wilt verwijderen"
    >
        <template #default>
            <text-heading>Weet u dit zeker?</text-heading>
            <p>
                U probeert iets te doen, dit kan niet teruggedraaid worden.<br>
                Weet u dit zeker?
            </p>
        </template>
        <template #actions="{ bindings }">
            <button-outline @click="bindings.overlay.closeOverlay();">
                Annuleren
            </button-outline>
            <button-block @loading="deleteRule($event, bindings.overlay.closeOverlay)">
                Bevestig
            </button-block>
        </template>
    </confirmation-dialog>
</template>

<script setup lang="ts">
    import EditIcon from '@keyvue/one-material-icons/icons/edit.svg?component';
    import RemoveIcon from '@keyvue/one-material-icons/icons/delete.svg?component';
    import { ButtonPlainIcon, Color, Size, type ExposedOverlay, type ButtonStateChangedEvent, ButtonState } from '@keyvue/one-ui';
    import { useRouter } from 'vue-router';
    import useClient from '@composables/useClient';
    import { Rule, DeleteRuleCommand } from '@keyvue/one-api-client';
    import useEventBus from '@composables/useEventBus';
    import { ref } from 'vue';
    import useNotify from '@composables/useNotify';

    const router = useRouter();
    const eventBus = useEventBus();
    const { client } = useClient();
    const { notifyDeleting } = useNotify();

    const deleteRuleConfirmDialog = ref<ExposedOverlay>();

    // @todo: gefixed met een typecast naar any, dit zou een reactive collectie moeten zijn
    // omdat het nu live moet, heb ik het zo gefixed
    const rules = ref(await client.getCollection(Rule)) as unknown as any;
    const deletableRule = ref<Rule | null>(null);

    eventBus.subscribe('resource-manipulated', async (params) => {
        if (params !== 'Rule') {
            return;
        }

        // @todo als dit een reactive collectie is dan kunnen we
        // ruleCollection.load() doen
        rules.value = await client.getCollection(Rule);
    });

    const confirmDeleteRule = (rule: Rule) => {
        deletableRule.value = rule;
        deleteRuleConfirmDialog.value?.openOverlay();
    };

    const deleteRule = async (event: ButtonStateChangedEvent, overlayCloseCallback: () => void) => {
        if (!deletableRule.value) {
            return;
        }

        const closeNotification = notifyDeleting(undefined, 0);

        const command = DeleteRuleCommand.fromRule(deletableRule.value as Rule);

        await client.dispatch(command);

        closeNotification();
        event.button.setState(ButtonState.completed);
        overlayCloseCallback();

        eventBus.dispatch('resource-manipulated', 'Rule');
    };
</script>

<style lang="scss" scoped>
.rule {
    line-height: size(md);
    border-bottom: 1px solid color(gray-200);
    padding-top: size(md);
    padding-bottom: size(md);
}
</style>
